import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, ArrowRight, ArrowLeft, Sparkle, Clock, Image as ImageIcon, CheckCircle, SpinnerGap } from "@phosphor-icons/react";
import { LogoUploader } from "./ImageUpload";
import HoursEditor from "./HoursEditor";

/**
 * Premium onboarding wizard — 3 steps shown once after trial/Premium activation.
 *
 * Step 1: Logo upload (visual identity)
 * Step 2: Opening hours (SEO + "ouvert maintenant" badge)
 * Step 3: AI-generated description (review + customize + save)
 *
 * Called when: business.premium.tier != 'free' AND !business.onboarding_completed_at.
 * On complete (or dismiss), sets onboarding_completed_at so the wizard never shows again.
 */
export default function OnboardingWizard({ biz, onClose, onBusinessUpdated }) {
  const [step, setStep] = useState(1);
  const [hours, setHours] = useState(biz?.hours || {});
  const [description, setDescription] = useState(biz?.description?.fr || "");
  const [aiBusy, setAiBusy] = useState(false);
  const [closing, setClosing] = useState(false);

  // Auto-generate a first description suggestion when arriving at step 3 (if empty)
  useEffect(() => {
    if (step === 3 && !description.trim() && !aiBusy) {
      generateAiDescription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const generateAiDescription = async () => {
    setAiBusy(true);
    try {
      const r = await api.post(`/dashboard/ai/${biz.slug}`, {
        tool: "description",
        input: { language: "fr" },
      });
      const text = r.data?.output || "";
      setDescription(text.trim());
      toast.success("Description générée par l'IA");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur IA");
    } finally {
      setAiBusy(false);
    }
  };

  const saveAndClose = async (opts = {}) => {
    setClosing(true);
    try {
      const payload = {};
      if (step >= 2 || opts.force) payload.hours = hours;
      if (step >= 3 || opts.force) {
        if (description.trim()) payload.description = { fr: description.trim() };
      }
      if (Object.keys(payload).length) {
        await api.put(`/dashboard/businesses/${biz.slug}`, payload);
      }
      await api.post(`/dashboard/businesses/${biz.slug}/onboarding/complete`);
      toast.success("Configuration enregistrée");
      onBusinessUpdated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur enregistrement");
      setClosing(false);
    }
  };

  const dismiss = async () => {
    // Silently mark completed so we don't show it again
    try {
      await api.post(`/dashboard/businesses/${biz.slug}/onboarding/complete`);
    } catch (err) {
      console.debug("[OnboardingWizard] mark complete failed", err);
    }
    onClose?.();
  };

  const next = () => setStep((s) => Math.min(3, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const STEPS = [
    { num: 1, label: "Logo", icon: ImageIcon },
    { num: 2, label: "Horaires", icon: Clock },
    { num: 3, label: "Description", icon: Sparkle },
  ];

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm grid place-items-center p-4 sm:p-8"
      data-testid="onboarding-wizard"
    >
      <div className="w-full max-w-2xl bg-card rounded-sm shadow-2xl border border-border overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-primary to-[#0f3d29] text-primary-foreground flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] opacity-75 mb-1">Configuration Premium · Étape {step}/3</div>
            <div className="font-display text-xl font-medium">{biz.name}</div>
          </div>
          <button
            onClick={dismiss}
            disabled={closing}
            className="opacity-70 hover:opacity-100"
            title="Remettre à plus tard"
            data-testid="onboarding-dismiss"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="flex border-b border-border bg-background/50">
          {STEPS.map(({ num, label, icon: Icon }) => (
            <div
              key={num}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs font-semibold border-b-2 ${
                step === num
                  ? "border-primary text-primary"
                  : step > num
                  ? "border-primary/30 text-primary/60"
                  : "border-transparent text-muted-foreground"
              }`}
              data-testid={`onboarding-step-indicator-${num}`}
            >
              {step > num ? <CheckCircle size={14} weight="fill" /> : <Icon size={14} weight="bold" />}
              <span className="hidden sm:inline">{label}</span>
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div data-testid="onboarding-step-1">
              <h2 className="font-display text-2xl font-medium tracking-tight mb-2">Ajoutez votre logo</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                Un logo professionnel multiplie la confiance des clients par 2. Il apparaît sur votre fiche, vos rapports mensuels, et votre badge Partenaire.
              </p>
              <LogoUploader slug={biz.slug} logo={biz.logo} onChange={(url) => onBusinessUpdated?.({ ...biz, logo: url })} />
              <div className="text-xs text-muted-foreground mt-4 italic">
                💡 Pas de logo ? Vous pouvez passer cette étape — vous pourrez l'ajouter plus tard.
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboarding-step-2">
              <h2 className="font-display text-2xl font-medium tracking-tight mb-2">Vos horaires d'ouverture</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                Les fiches avec horaires reçoivent <b>+73% de clics téléphone</b>. Google affiche aussi votre statut « ouvert maintenant » dans les résultats.
              </p>
              <HoursEditor hours={hours} onChange={setHours} />
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboarding-step-3">
              <div className="flex items-start justify-between gap-4 mb-2">
                <h2 className="font-display text-2xl font-medium tracking-tight">Votre description, rédigée par l'IA</h2>
                {!aiBusy && (
                  <button
                    onClick={generateAiDescription}
                    className="shrink-0 flex items-center gap-1.5 text-xs font-semibold border border-border hover:border-primary px-3 py-1.5 rounded-sm"
                    title="Régénérer"
                    data-testid="onboarding-regenerate-ai"
                  >
                    <Sparkle size={12} weight="fill" /> Régénérer
                  </button>
                )}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                L'IA a analysé votre catégorie et votre ville pour créer une première version. Ajustez-la à votre voix puis enregistrez.
              </p>
              {aiBusy ? (
                <div className="border border-border rounded-sm p-8 text-center text-sm text-muted-foreground bg-background/50" data-testid="onboarding-ai-loading">
                  <SpinnerGap size={24} className="mx-auto mb-3 animate-spin" />
                  L'IA rédige votre description…
                </div>
              ) : (
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={8}
                  placeholder="Votre description apparaîtra ici — ou rédigez la vôtre…"
                  className="w-full border border-border rounded-sm px-4 py-3 text-sm bg-background outline-none focus:border-primary leading-relaxed"
                  data-testid="onboarding-description-textarea"
                />
              )}
              <div className="text-xs text-muted-foreground mt-3 leading-relaxed">
                ✨ Astuce : gardez un ton authentique. Les clients sentent quand c'est trop marketing. {description ? `${description.length} caractères.` : ""}
              </div>
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border bg-background/50">
          <button
            onClick={dismiss}
            disabled={closing}
            className="text-sm text-muted-foreground hover:text-foreground"
            data-testid="onboarding-skip-all"
          >
            Configurer plus tard
          </button>
          <div className="flex items-center gap-2">
            {step > 1 && (
              <button
                onClick={prev}
                disabled={closing}
                className="flex items-center gap-1.5 px-4 py-2 border border-border rounded-sm text-sm font-semibold hover:bg-background"
                data-testid="onboarding-prev"
              >
                <ArrowLeft size={14} weight="bold" /> Précédent
              </button>
            )}
            {step < 3 ? (
              <button
                onClick={next}
                disabled={closing}
                className="flex items-center gap-1.5 bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-5 py-2 rounded-sm text-sm font-semibold"
                data-testid="onboarding-next"
              >
                Continuer <ArrowRight size={14} weight="bold" />
              </button>
            ) : (
              <button
                onClick={() => saveAndClose({ force: true })}
                disabled={closing}
                className="flex items-center gap-1.5 bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-5 py-2 rounded-sm text-sm font-semibold disabled:opacity-60"
                data-testid="onboarding-finish"
              >
                {closing ? "…" : "Terminer la configuration"}
                {!closing && <CheckCircle size={14} weight="bold" />}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
